import type { CoupleSpace } from '../../domain/types';
import { fromBase64Url, toBase64Url } from '../../protocol/base64url';
import { decodeInviteLink, encodeInviteLink, encodeMessageLink } from '../../protocol/links';
import { generateSpaceKey } from '../../protocol/crypto';
import type { AppRepositoryContract } from '../../storage/repository';

const DEFAULT_PARTNER_AVATAR = '🐻';

export interface CreateSpaceInput {
  spaceName: string;
  selfName: string;
  selfAvatar: string;
  partnerName: string;
  anniversary: string | null;
}

export interface AcceptInviteInput {
  selfName: string;
  selfAvatar: string;
}

export interface AcceptInviteOptions {
  replaceExisting?: boolean;
}

export interface PairingResult {
  space: CoupleSpace;
  inviteUrl: string;
}

export interface AcceptInviteResult {
  space: CoupleSpace;
  receiptUrl: string;
}

export class SpaceReplacementRequiredError extends Error {
  readonly code = 'SPACE_REPLACEMENT_REQUIRED';

  constructor(
    readonly currentSpaceName: string,
    readonly incomingSpaceName: string,
  ) {
    super(`本机已加入“${currentSpaceName}”，替换后会清除该空间的本机记录`);
    this.name = 'SpaceReplacementRequiredError';
  }
}

export class SpaceAlreadyExistsError extends Error {
  readonly code = 'SPACE_ALREADY_EXISTS';

  constructor(readonly currentSpaceName: string) {
    super(`本机已有“${currentSpaceName}”；请使用建立新空间的确认流程`);
    this.name = 'SpaceAlreadyExistsError';
  }
}

export class SpaceRoleConflictError extends Error {
  readonly code = 'SPACE_ROLE_CONFLICT';

  constructor() {
    super('本机已在这个双人空间中，不会更改固定身份');
    this.name = 'SpaceRoleConflictError';
  }
}

export class PairingService {
  constructor(
    private readonly repo: AppRepositoryContract,
    private readonly baseUrl: string,
  ) {}

  async createSpace(input: CreateSpaceInput): Promise<PairingResult> {
    const existing = await this.repo.getSpace();
    if (existing) throw new SpaceAlreadyExistsError(existing.spaceName);
    const normalized = normalizeCreateInput(input);
    const space = makeCreatorSpace(normalized, crypto.randomUUID(), generateSpaceKey());
    await this.repo.saveSpace(space);
    return { space, inviteUrl: encodeInviteLink(this.baseUrl, space) };
  }

  async acceptInvite(
    token: string,
    input: AcceptInviteInput,
    options: AcceptInviteOptions = {},
  ): Promise<AcceptInviteResult> {
    const invite = decodeInviteLink(token);
    const identity = normalizeIdentity(input);
    const existing = await this.repo.getSpace();

    if (existing) {
      if (existing.coupleId === invite.coupleId) throw new SpaceRoleConflictError();
      if (!options.replaceExisting) {
        throw new SpaceReplacementRequiredError(existing.spaceName, invite.spaceName);
      }
    }

    const at = new Date().toISOString();
    const space: CoupleSpace = {
      coupleId: invite.coupleId,
      memberId: 'partner',
      spaceName: invite.spaceName,
      selfName: identity.selfName,
      selfAvatar: identity.selfAvatar,
      partnerName: invite.inviterName,
      partnerAvatar: invite.inviterAvatar,
      anniversary: invite.anniversary,
      key: invite.key,
      createdAt: at,
    };

    if (existing && existing.coupleId !== invite.coupleId) {
      await this.repo.replaceWithFreshSpace(space);
    } else {
      await this.repo.saveSpace(space);
    }

    const receiptUrl = await encodeMessageLink(this.baseUrl, {
      kind: 'pairing-receipt',
      coupleId: space.coupleId,
      memberId: space.memberId,
      memberName: space.selfName,
      memberAvatar: space.selfAvatar,
      at,
    }, fromBase64Url(space.key));
    return { space, receiptUrl };
  }

  async regenerateInvite(): Promise<string> {
    return encodeInviteLink(this.baseUrl, await requireSpace(this.repo));
  }

  async createFreshSpace(input: CreateSpaceInput): Promise<PairingResult> {
    const normalized = normalizeCreateInput(input);
    const space = makeCreatorSpace(normalized, crypto.randomUUID(), generateSpaceKey());
    await this.repo.replaceWithFreshSpace(space);
    return { space, inviteUrl: encodeInviteLink(this.baseUrl, space) };
  }
}

export async function requireSpace(repo: AppRepositoryContract): Promise<CoupleSpace> {
  const space = await repo.getSpace();
  if (!space) throw new Error('本机还没有双人空间');
  return space;
}

function makeCreatorSpace(input: CreateSpaceInput, coupleId: string, key: Uint8Array): CoupleSpace {
  return {
    coupleId,
    memberId: 'creator',
    spaceName: input.spaceName,
    selfName: input.selfName,
    selfAvatar: input.selfAvatar,
    partnerName: input.partnerName,
    partnerAvatar: DEFAULT_PARTNER_AVATAR,
    anniversary: input.anniversary,
    key: toBase64Url(key),
    createdAt: new Date().toISOString(),
  };
}

function normalizeCreateInput(input: CreateSpaceInput): CreateSpaceInput {
  const spaceName = requireShortText(input.spaceName, '双人空间名称');
  const identity = normalizeIdentity(input);
  const partnerName = requireShortText(input.partnerName, 'TA 的昵称');
  return {
    spaceName,
    selfName: identity.selfName,
    selfAvatar: identity.selfAvatar,
    partnerName,
    anniversary: input.anniversary || null,
  };
}

function normalizeIdentity(input: AcceptInviteInput): AcceptInviteInput {
  const selfName = requireShortText(input.selfName, '你的昵称');
  const selfAvatar = input.selfAvatar.trim();
  if (!selfAvatar) throw new TypeError('请选择你的头像');
  return { selfName, selfAvatar };
}

function requireShortText(value: string, label: string): string {
  const normalized = value.trim();
  if (!normalized) throw new TypeError(`请填写${label}`);
  if ([...normalized].length > 20) throw new TypeError(`${label}最多 20 个字`);
  return normalized;
}
